import {config} from '../init/config';
import {StorageObjectMetadata} from '../models/StorageObjectMetadata';
import {Label} from '../models/Label';
import {ObjectType} from '../models/ObjectType';
import {StorageObject} from '../models/StorageObject';
import {
    convertToStringArray,
    getEffectiveDate,
    getPath,
    isNullOrEmpty,
    isTrue,
    isValidBoolean,
    isValidNumber,
    isValidStringArray,
    isValidText
} from './HelperUtils';
import {TemplatePropertySettings} from '../models/TemplatePropertySettings';
import {getDefaultValueOfComponent, isMandatoryComponent} from './TemplateUtils';
import {
    STORAGE_OBJECT_OBJECT_IS_PUBLIC,
    STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION,
    STORAGE_OBJECT_OBJECT_LABELS_NAME,
    STORAGE_OBJECT_OBJECT_METADATA_COMMENT,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_FROM,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_TO,
    STORAGE_OBJECT_OBJECT_METADATA_DISCLAIMER,
    STORAGE_OBJECT_OBJECT_METADATA_OWNER_ROLES,
    STORAGE_OBJECT_OBJECT_METADATA_ROLES,
    STORAGE_OBJECT_OBJECT_METADATA_TAGS,
    STORAGE_OBJECT_OBJECT_METADATA_USER_LABELS,
    STORAGE_OBJECT_OBJECT_METADATA_VERSION,
    STORAGE_OBJECT_OBJECT_METADATA_WATERMARK
} from '../init/storage_object_constants';
import {NO_DISCLAIMER_KEY} from '../init/constants';
import {StorageObjectDialogProps} from '../components/dialogs/StorageObjectDialogProps';
import cloneDeep from 'lodash/cloneDeep';

export const getEmptyStorageObject = (objectType: ObjectType): StorageObject => {
    let storageObject = {} as StorageObject;
    storageObject.partitionKey = config.DLS_SPACE_NAME;
    storageObject.metadata = getEmptyStorageObjectMetadata();
    storageObject.objectType = objectType;
    storageObject.labels = new Map() as Map<string, Label>;

    return storageObject;
};

export const getEmptyStorageObjectMetadata = (): StorageObjectMetadata => {
    return {} as StorageObjectMetadata;
};

export const getEmptyLabel = (): Label => {
    return {name: '', description: ''} as Label;
};

export const getInitializedLabel = (name: string): Label => {
    if ((name !== undefined) && (!isNullOrEmpty(name))) {
        return {name: name, description: ''} as Label;
    }

    return getEmptyLabel();
};

export const getEmptyLabelEntryMap = (locale: string): Map<string, Label> => {
    let labelMap = new Map() as Map<string, Label>;
    if ((locale === undefined) || (isNullOrEmpty(locale))) {
        return labelMap;
    }

    let mapEntries: [string, Label][] = [];
    mapEntries.push([locale, getEmptyLabel()]);
    labelMap = new Map(mapEntries) as Map<string, Label>;

    return labelMap as Map<string, Label>;
};

export const getLabelMap = (locale: string, labels: Map<string, Label>): Map<string, Label> => {
    if ((labels === undefined) || (!labels)) {
        getEmptyLabelEntryMap(locale);
    }

    return labels;
};

export const getLabelEntryMap = (locale: string, labels: Map<string, Label>): Map<string, Label> => {
    let labelMap = getLabelMap(locale, labels);
    return new Map(Object.entries(labelMap)) as Map<string, Label>;
};

export const getLabelEntryMapDefault = (locale: string, labels: Map<string, Label> | null | undefined): Map<string, Label> => {
    if (((labels !== undefined) && labels)
        && ((labels.size > 0) || ((Symbol.iterator in Object.keys(labels)) && (Object.keys(labels).length > 0)))) {
        let labelMap = getLabelEntryMap(locale, labels);
        if ((labelMap.size > 0) || ((Symbol.iterator in Object.keys(labelMap)) && (Object.keys(labelMap).length > 0))) {
            return labelMap;
        } else {
            return labels;
        }
    }

    return getEmptyLabelEntryMap(locale);
};

export const getLabelEntryMapInitialized = (locale: string, name: string | null | undefined, labels: Map<string, Label> | null | undefined): Map<string, Label> => {
    if (((labels !== undefined) && labels)
        && ((labels.size > 0) || ((Symbol.iterator in Object.keys(labels)) && (Object.keys(labels).length > 0)))) {
        return getLabelEntryMapDefault(locale, labels);
    }

    if ((name !== undefined) && name && (!isNullOrEmpty(name.trim()))) {
        let mapEntries: [string, Label][] = [];
        mapEntries.push([locale, getInitializedLabel(name.trim())]);
        let labelMap = new Map(mapEntries) as Map<string, Label>;

        return labelMap as Map<string, Label>;
    }

    return getEmptyLabelEntryMap(locale);
};

export const getLabel = (locale: string, labels: Map<string, Label>): Label => {
    if ((locale === undefined) || (labels === undefined) || (isNullOrEmpty(locale)) || (!labels)) {
        return getEmptyLabel();
    }

    let labelMap = getLabelEntryMap(locale, labels);
    let label = labelMap.get(locale);
    if ((label === undefined) || (!label)) {
        label = getEmptyLabel();
    }

    return label as Label;
};

export const getLabelNative = (locale: string, labels: Map<string, Label>): Label => {
    if ((locale === undefined) || (labels === undefined) || (isNullOrEmpty(locale)) || (!labels)) {
        return getEmptyLabel();
    }

    let labelMap = getLabelMap(locale, labels);
    let label = labelMap.get(locale);
    if ((label === undefined) || (!label)) {
        label = getEmptyLabel();
    }

    return label as Label;
};

export const getLabels = (labels: Map<string, Label>): Label[] => {
    let labelResult: Label[] = [];
    if ((labels === undefined) || (!labels)) {
        return labelResult;
    }

    Object.values(labels).map(value => labelResult.push({name: value.name, description: value.description} as Label));

    return labelResult as Label[];
};

export const getLabelMapKeys = (locale: string, labels: Map<string, Label> | null | undefined): string[] => {
    if ((labels !== undefined) && labels) {
        if (labels.size > 0) {
            return [...Object.keys(Object.fromEntries(labels))];
        } else if ((Symbol.iterator in Object.keys(labels)) && (Object.keys(labels).length > 0)) {
            return [...Object.keys(labels)];
        }
    }

    if ((locale !== undefined) && (!isNullOrEmpty(locale))) {
        return [...Object.keys(Object.fromEntries(getEmptyLabelEntryMap(locale)))];
    }

    return [];
};

export const getLabelsAsObjectMap = (labels: Map<string, Label> | null | undefined) => {
    let labelMap = new Map();
    if ((labels === undefined) || (!labels)) {
        return labelMap;
    }

    labels.forEach((value, key) =>
        labelMap.set(key,
            {
                name: (isValidText(value.name)) ? value.name.trim() : '',
                description: (isValidText(value.description)) ? value.description.trim() : ''
            } as Label));

    return Object.fromEntries(labelMap);
    // labels.forEach((value, key) => labelMap.set(key, value));
    // return Object.fromEntries(labelMap);
};

export const updateLabelEntryMap = (locale: string, label: Label, labels: Map<string, Label>): Map<string, Label> => {
    let labelMap = getLabelMap(locale, labels);
    if ((locale !== undefined) && (label !== undefined) && (!isNullOrEmpty(locale)) && label) {
        let mapEntries: [string, Label][] = [];
        Object.keys(Object.fromEntries(labelMap)).map(key => {
                if (key === locale) {
                    mapEntries.push([key, {name: label.name, description: label.description} as Label]);
                } else {
                    let value = labelMap.get(key);
                    mapEntries.push([key, value ? {
                        name: value.name,
                        description: value.description
                    } as Label : getEmptyLabel()]);
                }
            }
        );

        labelMap = new Map(mapEntries) as Map<string, Label>;
    }

    return labelMap as Map<string, Label>;
};

export const setLabelEntryMap = (locales: string[], labels: Map<string, Label>): Map<string, Label> => {
    let labelMap = new Map() as Map<string, Label>;
    if ((locales !== undefined) && (labels !== undefined) && locales && labels) {
        let mapEntries: [string, Label][] = [];
        locales.map(key => {
                if ((key !== undefined) && (!isNullOrEmpty(key))) {
                    if (labels.has(key)) {
                        let value = labels.get(key);
                        mapEntries.push([key, value ? {
                            name: value.name,
                            description: value.description
                        } as Label : getEmptyLabel()]);
                    } else {
                        mapEntries.push([key, getEmptyLabel()]);
                    }
                }
            }
        );

        labelMap = new Map(mapEntries) as Map<string, Label>;
    }

    return labelMap as Map<string, Label>;
};

export const spreadLabelEntryMap = (locale: string, label: Label, labels: Map<string, Label>): Map<string, Label> => {
    let labelMap = getLabelMap(locale, labels);
    if ((locale !== undefined) && (!isNullOrEmpty(locale))) {
        let mapEntries: [string, Label][] = [];
        Object.keys(Object.fromEntries(labelMap)).map(key => {
            mapEntries.push([key, {name: label.name, description: label.description} as Label]);
        });

        labelMap = new Map(mapEntries) as Map<string, Label>;
    }

    return labelMap as Map<string, Label>;
};

export const applyLabelEntryMapTemplateDefaults = (locale: string, labels: Map<string, Label>, templatePropertySettings: Map<string, TemplatePropertySettings>): Map<string, Label> => {
    let labelMap = getLabelMap(locale, labels);
    if ((locale !== undefined) && (!isNullOrEmpty(locale))) {
        let label = {
            name: getDefaultValueOfComponent(STORAGE_OBJECT_OBJECT_LABELS_NAME, templatePropertySettings),
            description: getDefaultValueOfComponent(STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION, templatePropertySettings)
        } as Label;

        let mapEntries: [string, Label][] = [];
        Object.keys(Object.fromEntries(labelMap)).map(key => {
            let value = labelMap.get(key);
            mapEntries.push([key, {
                name: (isValidText(value?.name)) ? value?.name : label.name,
                description: (isValidText(value?.description)) ? value?.description : label.description
            } as Label]);
        });

        labelMap = new Map(mapEntries) as Map<string, Label>;
    }

    return labelMap as Map<string, Label>;
};

export const addLabelEntryMap = (locale: string, localeToAdd: string, labels: Map<string, Label>): Map<string, Label> => {
    let labelMap = getLabelMap(locale, labels);
    if ((locale !== undefined) && (!isNullOrEmpty(locale)) && (localeToAdd !== undefined) && (!isNullOrEmpty(localeToAdd))) {
        let mapEntries: [string, Label][] = [];
        let keys = Object.keys(Object.fromEntries(labelMap));
        keys.map(key => {
            let value = labelMap.get(key);
            mapEntries.push([key, value ? {
                name: value.name,
                description: value.description
            } as Label : getEmptyLabel()]);
        });

        if (!keys.includes(localeToAdd)) {
            mapEntries.push([localeToAdd, getEmptyLabel()]);
        }

        labelMap = new Map(mapEntries) as Map<string, Label>;
    }

    return labelMap as Map<string, Label>;
};

export const addAllLabelEntryMap = (locale: string, localesToAdd: string[], labels: Map<string, Label>): Map<string, Label> => {
    let labelMap = getLabelMap(locale, labels);
    if ((locale !== undefined) && (!isNullOrEmpty(locale)) && (localesToAdd !== undefined) && localesToAdd && (localesToAdd.length > 0)) {
        let mapEntries: [string, Label][] = [];
        let keys = Object.keys(Object.fromEntries(labelMap));
        keys.map(key => {
            let value = labelMap.get(key);
            mapEntries.push([key, value ? {
                name: value.name,
                description: value.description
            } as Label : getEmptyLabel()]);
        });

        localesToAdd.map(localeToAdd => {
            if (!keys.includes(localeToAdd)) {
                mapEntries.push([localeToAdd, getEmptyLabel()]);
            }
        });

        labelMap = new Map(mapEntries) as Map<string, Label>;
    }

    return labelMap as Map<string, Label>;
};

export const deleteLabelEntryMap = (locale: string, labels: Map<string, Label>): Map<string, Label> => {
    let labelMap = getLabelMap(locale, labels);
    if ((locale !== undefined) && (!isNullOrEmpty(locale))) {
        let mapEntries: [string, Label][] = [];
        Object.keys(Object.fromEntries(labelMap)).map(key => {
            if (key !== locale) {
                let value = labelMap.get(key);
                mapEntries.push([key, value ? {
                    name: value.name,
                    description: value.description
                } as Label : getEmptyLabel()]);
            }
        });

        labelMap = new Map(mapEntries) as Map<string, Label>;
    }

    return labelMap as Map<string, Label>;
};

export const validateLabelEntryMap = (labels: Map<string, Label> | null | undefined, templatePropertySettings: Map<string, TemplatePropertySettings>): boolean => {
    if ((labels === undefined) || (!labels)) {
        return false;
    }

    let result = true;
    labels.forEach((value) => {
        result = result && (isMandatoryComponent(STORAGE_OBJECT_OBJECT_LABELS_NAME, templatePropertySettings) ? isValidText(value.name) : true)
            && (isMandatoryComponent(STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION, templatePropertySettings) ? isValidText(value.description) : true);
    });

    return result;
};

export const initializeStorageObject = (props: StorageObjectDialogProps, objectType: ObjectType = ObjectType.FOLDER, file?: File | null): StorageObject => {
    let initializedItem = ((props.item !== undefined) && props.item) ? cloneDeep(props.item) : getEmptyStorageObject(objectType);
    if ((initializedItem) && (!isValidText(initializedItem.id))) {
        if (isFile(initializedItem.objectType)) {
            if (!isValidText(initializedItem.name)) {
                initializedItem.name = file?.name ?? '';
            }

            if (!isValidNumber(initializedItem.totalSize)) {
                initializedItem.totalSize = file?.size ?? 0;
            }

            initializedItem.metadata.contentType = file?.type;
        } else {
            if (!isValidText(initializedItem.name)) {
                initializedItem.name = props.name ?? '';
            }
        }

        initializedItem.path = decodeURI(props.currentPath);
        initializedItem.fullPath = getPath(initializedItem.path, initializedItem.name);
        if (!isValidNumber(initializedItem.metadata.dateValidFrom)) {
            initializedItem.metadata.dateValidFrom = getEffectiveDate().getTime();
        }
    }

    initializedItem.labels = getLabelEntryMapInitialized(props.defaultLocale, initializedItem.name, initializedItem.labels);
    initializedItem.metadata.parentOwnerRoles = props.parentOwnerRoles ?? [];
    initializedItem.metadata.parentRoles = props.parentRoles ?? [];

    return initializedItem;
};

export const applyStorageObjectTemplateDefaults = (storageObject: StorageObject, defaultLocale: string, templatePropertySettings: Map<string, TemplatePropertySettings>): StorageObject => {
    if ((storageObject === undefined) || (!storageObject)) {
        return storageObject;
    }

    if (isValidText(storageObject.id)) {
        return storageObject;
    }

    if (!isValidBoolean(storageObject.isPublic)) {
        storageObject.isPublic = isTrue(getDefaultValueOfComponent(STORAGE_OBJECT_OBJECT_IS_PUBLIC, templatePropertySettings));
    }

    if (!isValidText(storageObject.metadata.version)) {
        storageObject.metadata.version = getDefaultValueOfComponent(STORAGE_OBJECT_OBJECT_METADATA_VERSION, templatePropertySettings);
    }

    if (!isValidText(storageObject.metadata.comment)) {
        storageObject.metadata.comment = getDefaultValueOfComponent(STORAGE_OBJECT_OBJECT_METADATA_COMMENT, templatePropertySettings);
    }

    storageObject.labels = applyLabelEntryMapTemplateDefaults(defaultLocale, storageObject.labels, templatePropertySettings);
    if (!isValidStringArray(storageObject.metadata.ownerRoles)) {
        storageObject.metadata.ownerRoles = convertToStringArray([getDefaultValueOfComponent(STORAGE_OBJECT_OBJECT_METADATA_OWNER_ROLES, templatePropertySettings)]);
    }

    if (!isValidStringArray(storageObject.metadata.roles)) {
        storageObject.metadata.roles = convertToStringArray([getDefaultValueOfComponent(STORAGE_OBJECT_OBJECT_METADATA_ROLES, templatePropertySettings)]);
    }

    if (isFile(storageObject.objectType)) {
        if (!isValidBoolean(storageObject.metadata.watermark)) {
            storageObject.metadata.watermark = isTrue(getDefaultValueOfComponent(STORAGE_OBJECT_OBJECT_METADATA_WATERMARK, templatePropertySettings));
        }

        if (!isValidText(storageObject.metadata.disclaimer)) {
            let defaultDisclaimerValue = getDefaultValueOfComponent(STORAGE_OBJECT_OBJECT_METADATA_DISCLAIMER, templatePropertySettings);
            if (!isValidText(defaultDisclaimerValue)) {
                defaultDisclaimerValue = NO_DISCLAIMER_KEY;
            }

            storageObject.metadata.disclaimer = defaultDisclaimerValue;
        }

        if (!isValidStringArray(storageObject.metadata.userLabels)) {
            storageObject.metadata.userLabels = convertToStringArray([getDefaultValueOfComponent(STORAGE_OBJECT_OBJECT_METADATA_USER_LABELS, templatePropertySettings)]);
        }
    }

    return storageObject
};

export const configureStorageObject = (storageObject: StorageObject, defaultLocale: string, templatePropertySettings: Map<string, TemplatePropertySettings>): StorageObject => {
    if (!storageObject) {
        return storageObject;
    }

    if (!isValidText(storageObject.id)) {
        return applyStorageObjectTemplateDefaults(storageObject, defaultLocale, templatePropertySettings);
    } else {
        if (!isValidBoolean(storageObject.isPublic)) {
            storageObject.isPublic = false;
        }

        if (isFile(storageObject.objectType)) {
            if (!isValidBoolean(storageObject.metadata.watermark)) {
                storageObject.metadata.watermark = false;
            }
        }
    }

    return storageObject;
};

export const getStorageObjectFullyConfigured = (props: StorageObjectDialogProps, objectType: ObjectType = ObjectType.FOLDER, templatePropertySettings: Map<string, TemplatePropertySettings>, file?: File | null): StorageObject => {
    return configureStorageObject(initializeStorageObject(props, objectType, file), props.defaultLocale, templatePropertySettings);
};

export const isValidStorageObjectCommons = (storageObject: StorageObject, templatePropertySettings: Map<string, TemplatePropertySettings>): boolean => {
    return ((isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_VERSION, templatePropertySettings) ? isValidText(storageObject.metadata.version) : true)
        && validateLabelEntryMap(storageObject.labels, templatePropertySettings)
        && (isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_COMMENT, templatePropertySettings) ? isValidText(storageObject.metadata.comment) : true)
        && (isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_FROM, templatePropertySettings) ? isValidNumber(storageObject.metadata.dateValidFrom) : true)
        && (isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_TO, templatePropertySettings) ? isValidNumber(storageObject.metadata.dateValidTo) : true)
        && (isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_TAGS, templatePropertySettings) ? isValidStringArray(storageObject.metadata.tags) : true)
        && (isMandatoryComponent(STORAGE_OBJECT_OBJECT_IS_PUBLIC, templatePropertySettings) ? isValidBoolean(storageObject.isPublic) : true)
        && (isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_ROLES, templatePropertySettings) ? isValidStringArray(storageObject.metadata.roles) : true)
        && (isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_OWNER_ROLES, templatePropertySettings) ? isValidStringArray(storageObject.metadata.ownerRoles) : true)) as boolean;
};

export const isFile = (objectType: ObjectType): boolean => {
    return (ObjectType.FILE === objectType) as boolean;
};

export const isFolder = (objectType: ObjectType): boolean => {
    return (ObjectType.FOLDER === objectType) as boolean;
};
